import React, { useEffect, useState } from "react";

import {
  Avatar,
  CardHeader,
  Chip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  wrapper: {
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(19,49,43,0.98) 0%, rgba(11,23,20,0.96) 100%)"
        : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(247,250,255,0.95) 100%)",
    borderBottom: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(52, 211, 153, 0.10)"
        : "rgba(148, 163, 184, 0.18)"
    }`,
    boxShadow: "none",
    borderRadius: 12,
    padding: "4px 6px"
  },
  avatar: {
    width: 42,
    height: 42,
    border: "2px solid rgba(255,255,255,0.92)",
    boxShadow: "0 10px 20px rgba(15, 23, 42, 0.12)"
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0
  },
  titleText: {
    fontWeight: 700,
    fontSize: "0.98rem",
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  ticketBadge: {
    background:
      "linear-gradient(180deg, rgba(255,247,204,1) 0%, rgba(255,239,169,1) 100%)",
    color: "#8a6300",
    border: "1px solid rgba(226, 192, 84, 0.55)",
    borderRadius: 999,
    padding: "3px 8px",
    fontSize: "0.76rem",
    fontWeight: 700,
    flex: "none",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.75)"
  },
  groupChip: {
    height: 22,
    fontWeight: 700,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(52, 211, 153, 0.14)"
        : "rgba(18, 140, 126, 0.12)",
    color: theme.palette.primary.main
  },
  queueChip: {
    height: 22,
    fontWeight: 700,
    maxWidth: 140
  },
  tagChip: {
    height: 22,
    fontWeight: 700
  },
  subheader: {
    color: theme.palette.text.secondary,
    fontWeight: 500
  },
  subheaderContent: {
    display: "flex",
    flexDirection: "column",
    gap: 4
  },
  metricsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 5
  },
  metricChip: {
    height: 22,
    fontWeight: 700
  },
  slaPausedChip: {
    height: 22,
    fontWeight: 700,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(245, 158, 11, 0.20)"
        : "rgba(245, 158, 11, 0.16)",
    color: theme.palette.type === "dark" ? "#fbbf24" : "#b45309"
  }
}));

const formatTicketNumber = ticketId =>
  `#${String(ticketId || "").padStart(6, "0")}`;

const parseDateOrNull = value => {
  if (!value) {
    return null;
  }
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const pad = value => String(value).padStart(2, "0");

const formatElapsed = value => {
  const milliseconds = Number(value || 0);
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) {
    return "00:00:00";
  }

  const totalSeconds = Math.floor(milliseconds / 1000);
  const seconds = totalSeconds % 60;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const minutes = totalMinutes % 60;
  const totalHours = Math.floor(totalMinutes / 60);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;

  if (days > 0) {
    return `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }

  return `${pad(totalHours)}:${pad(minutes)}:${pad(seconds)}`;
};

const TicketInfo = ({
  contact,
  ticket,
  onClick
}) => {
  const classes = useStyles();
  const [nowTimestamp, setNowTimestamp] = useState(Date.now());

  useEffect(() => {
    const intervalId = setInterval(() => {
      setNowTimestamp(Date.now());
    }, 1000);
    return () => clearInterval(intervalId);
  }, []);

  const title = (
    <span className={classes.titleRow}>
      <span className={classes.ticketBadge}>
        {formatTicketNumber(ticket.id)}
      </span>
      <span className={classes.titleText}>{contact.name}</span>
      {ticket.isGroup && (
        <Chip size="small" label="Grupo" className={classes.groupChip} />
      )}
      {ticket?.queue?.name && (
        <Chip
          size="small"
          label={`Fila: ${ticket.queue.name}`}
          className={classes.queueChip}
          style={{
            backgroundColor: `${ticket.queue.color || "#64748b"}22`,
            color: ticket.queue.color || "#334155"
          }}
        />
      )}
      {Array.isArray(ticket.tags) &&
        ticket.tags.slice(0, 2).map(tag => (
          <Chip
            key={tag.id}
            size="small"
            label={tag.name}
            className={classes.tagChip}
            style={{
              backgroundColor: `${tag.color}22`,
              color: tag.color
            }}
          />
        ))}
    </span>
  );

  const assignedLine =
    ticket.user &&
    `${i18n.t("messagesList.header.assignedTo")} ${ticket.user.name}`;

  const createdAt = parseDateOrNull(ticket?.createdAt);
  const closedAt = parseDateOrNull(ticket?.closedAt || ticket?.updatedAt);
  const pausedAt = parseDateOrNull(ticket?.slaPausedAt);
  const pausedTotalMs = Number(ticket?.slaPausedTotalMs || 0);
  const closedReferenceTimestamp =
    closedAt?.getTime() || createdAt?.getTime() || nowTimestamp;
  const endTimestamp =
    ticket?.status === "closed" ? closedReferenceTimestamp : nowTimestamp;
  const atendimentoMs = createdAt
    ? Math.max(0, endTimestamp - createdAt.getTime())
    : 0;
  const ongoingPauseMs = pausedAt
    ? Math.max(0, nowTimestamp - pausedAt.getTime())
    : 0;
  const totalPausedMs = Math.max(0, pausedTotalMs + ongoingPauseMs);
  const slaMs = Math.max(0, atendimentoMs - totalPausedMs);

  const subheader = (
    <div className={classes.subheaderContent}>
      {assignedLine ? <span>{assignedLine}</span> : null}
      <div className={classes.metricsRow}>
        <Chip
          size="small"
          className={classes.metricChip}
          label={`Tempo atendimento: ${formatElapsed(atendimentoMs)}`}
        />
        <Chip
          size="small"
          className={classes.metricChip}
          label={`Tempo SLA: ${formatElapsed(slaMs)}`}
        />
        {pausedAt ? (
          <Chip
            size="small"
            className={classes.slaPausedChip}
            label="SLA pausado"
          />
        ) : null}
      </div>
    </div>
  );

  return (
    <CardHeader
      onClick={onClick}
      className={classes.wrapper}
      disableTypography
      titleTypographyProps={{ component: "div" }}
      subheaderTypographyProps={{ component: "div", className: classes.subheader }}
      style={{ cursor: "pointer", padding: "6px 8px" }}
      avatar={
        <Avatar
          src={contact.profilePicUrl}
          alt="contact_image"
          className={classes.avatar}
        />
      }
      title={title}
      subheader={subheader}
    />
  );
};

export default TicketInfo;
